import { LocMarkupBase } from './loc_markup_base.js';
import { ReviewRatingsViewMarkup } from './review_ratings_view_markup.js';

/**
 * Location view widget markup generator.
 */
class LocMarkupView extends LocMarkupBase {
  static get selectors() {
    const Availability = '.locview-availability';
    const FieldsContainer = '.locview-fields-container';
    const History = '.locview-history';
    const MapContainer = '.locview-map-container';
    const ReviewsContainer = `.locview-reviews-container`;
    const ReviewItemsContainer = `${ReviewsContainer} > .locview-review-items-container`;
    const Summary = '.locview-summary';
    const TitleContainer = '.locview-title-container';
    return Object.assign({}, super.selectors, {
      Availability,
      AvailabilityCalendar: `${Availability} > calendar-control`,
      BackButton: '.locview-backbutton',
      CloseButton: '.locview-closebutton',
      Description: '.locview-description',
      ExpandButton: '.locview-expandbutton',
      FieldsContainer,
      FieldItems: `${FieldsContainer} > *`,
      FieldLinks: `${FieldsContainer} > .locview-link`,
      FieldTexts: `${FieldsContainer} > .span`,
      GoingThereButton: '.going-there-button',
      GoogleMapsLink: '.google-link',
      History,
      HistoryContent: `${History} > ul`,
      LocDataContainer: '.locview-data',
      MapControlsBar: `${MapContainer} > map-controlsbar`,
      MapHolder: `${MapContainer} > .locview-map-holder`,
      MosaicCover: 'image-mosaic',
      OverallRating: `[rating-id=overall]`,
      OverallRatingLabel: `.locview-overall-rating-label`,
      ShareLocButton: '.share-loc-button',
      SocialMediaContainer: `${FieldsContainer} > .ico-link-container`,
      SuggestEditButton: '.suggest-edit-button',
      RatingLabel: `${TitleContainer} .locview-rating`,
      RatingControl: `${TitleContainer} rating-control`,
      RelsContainer: `.locview-rels`,
      RelLinkFirst: '.locview-rel-link:first-child',
      RelLinks: '.locview-rel-link',
      ReviewsContainer,
      ReviewItems: `${ReviewItemsContainer} review-view`,
      ReviewItemsContainer,
      PicList: `.locview-piclist`,
      Summary,
      SummaryGroupLabels: `${Summary} ${ReviewRatingsViewMarkup.selectors.RatingGroupLabels}`,
      SummaryGroupValues: `${Summary} ${ReviewRatingsViewMarkup.selectors.RatingGroupValues}`,
      TitleContainer,
      Title: `${TitleContainer} .locview-title`,
      Type: `${TitleContainer} .locview-type`,
      WriteReviewButton: '.write-review-button',
    });
  }

  static filterDynamicSnippets(snippets) {
    const allDynamicSnippets = ['title'];
    return allDynamicSnippets.filter(snippet => snippets.includes(snippet));
  }

  constructor(loc, { assert, log, typeOf }) {
    super(loc);

    if (!assert) throw new Error(`LocPage: no ${assert} function is given`);
    this.assert = assert;

    if (!log) throw new Error(`LocPage: no ${log} function is given`);
    this.log = log;

    if (!typeOf) throw new Error(`LocPage: no ${typeOf} function is given`);
    this.typeOf = typeOf;
  }

  get shortyMarkup() {
    return `
      <div class='locview-panel-header'>
        ${this.titleMarkup}
        <img class='locview-expandbutton'
             src='/images/map-arrow-up.png'>
      </div>
      <div class='locview-footer'>${this.mainButtonsMarkup}</div>
    `;
  }

  get shortyMarkupSnippets() {
    return ['mainButtons', 'title'];
  }

  get regularMarkup() {
    return `
      ${this.coverMarkup}
      <div class='locview-content'>
        ${this.titleMarkup}
        ${this.descriptionMarkup}
        ${this.picListMarkup}
        ${this.availabilityMarkup}
        ${this.summaryMarkup}
        <div class='loading-box-right'>
          <div class='loading-bar40'></div>
          <div class='loading-bar30'></div>
          <div class='loading-bar40'></div>
        </div>
        <div class='locview-fields-container content-box'>
          ${this.fieldsMarkup}
          ${this.googleMapsLinkMarkup}
        </div>
        ${this.reviewsMarkup}
        <details class='locview-history show-if-logged-in'>
          <summary class='locview-section-label'>History</summary>
          <ul>${this.historyMarkup}</ul>
        </details>
        ${this.editButtons}
      </div>
    `;
  }

  get regularMarkupSnippets() {
    return [
      'availability',
      'cover',
      'description',
      'editButtons',
      'fields',
      'history',
      'googleMapsLink',
      'mainButtons',
      'picList',
      'reviews',
      'summary',
      'title',
    ];
  }

  get fullMarkup() {
    return `
      ${this.titleMarkup}
      ${this.mosaicCoverMarkup}
      <div class='locview-content'>
        ${this.fullDescriptionMarkup}
        ${this.summaryMarkup}
        ${this.availabilityMarkup}
        <div class='locview-fields-container'>
          ${this.fieldsMarkup}
        </div>
      </div>
      <div class='locview-map-container'>
        <div class='locview-map-holder'></div>
        <map-controlsbar controls='enlarge zoom'></map-controlsbar>
      </div>
      <div class='locview-fields-container'>${this.googleMapsLinkMarkup}</div>
      <div class='locview-data' hidden>${this.#locDataStringified}</div>
    `;
  }

  get fullMarkupSnippets() {
    return [
      'availability',
      'fields',
      'fullDescription',
      'googleMapsLink',
      'mosaicCover',
      'summary',
      'title',
    ];
  }

  get availabilityMarkup() {
    let { calendar } = this.loc;
    let maybeHiddenAttr = calendar ? '' : 'hidden';
    let maybeValueAttr = calendar ? `value='${JSON.stringify(calendar)}'` : '';
    return `
      <div class='locview-availability' ${maybeHiddenAttr}>
        <label class='locview-section-label'>Availability</label>
        <calendar-control ${maybeValueAttr}></calendar-control>
      </div>
    `;
  }

  get coverButtonsMarkup() {
    return `
      <img class='locview-closebutton controls'
           src='/images/map-button-close.png'
           title='Close the panel'>
      <span class='locview-backbutton locview-round-button'
            title='Close the panel'></span>
      ${this.mainButtonsMarkup}
    `;
  }

  get descriptionMarkup() {
    return `
      <div class='loading-box'>
        <div class='loading-bar90'></div>
        <div class='loading-bar90'></div>
        <div class='loading-bar80'></div>
        <div class='loading-bar90'></div>
        <div class='loading-bar40'></div>
      </div>
      <collapsible-text class='locview-description content-box'
                        value='${this.loc.description}'></collapsible-text>
    `;
  }

  get editButtons() {
    return `
      <div class='locview-buttons'>
        <div class='cute-button write-review-button'
             tabindex='0'>Write a review</div>
        <div class='cute-button suggest-edit-button'
             tabindex='0'>Suggest an edit</div>
      </div>
    `;
  }

  get fieldsMarkup() {
    const { FieldTypes } = this.loc.constructor;

    let linksMarkup = '';
    let socialMediaMarkup = '';

    let types = new Set();
    for (let { name, type, value } of this.loc.typedFields) {
      switch (type) {
        case FieldTypes.SocialMedia:
          socialMediaMarkup += this.#getSocialMediaFieldMarkup(name, value);
          break;

        case FieldTypes.Address:
          linksMarkup += `<span>${value}</span>`;
          break;

        case FieldTypes.Email:
          linksMarkup += `
            <a href='mailto:${value}'
               target='_blank' rel='noopener'
               class='locview-link'>${value}</a>
          `;
          break;

        case FieldTypes.Phone:
          linksMarkup += `
            <a href='tel:${value}' target='_blank' rel='noopener'
               class='locview-link' title='Phone number'>
              ${!types.has(type) ? "<img src='/images/map-phone.png'>" : ''}
              ${value}
            </a>`;
          break;

        case FieldTypes.Registration:
          linksMarkup += this.#getGenericFieldMarkup(
            name,
            value,
            types.has(type) ? '' : '/images/map-cashbook.png'
          );
          break;

        case FieldTypes.Website:
          linksMarkup += `<a href='${value}' target='_blank' rel='noopener'
                class='locview-link'>
              ${!types.has(type) ? "<img src='/images/map-geography.png'>" : ''}
              ${new URL(value).hostname}
            </a>`;
          break;

        default:
          linksMarkup += this.#getGenericFieldMarkup(name, value);
      }
      types.add(type);
    }

    if (socialMediaMarkup) {
      socialMediaMarkup = `<div class='ico-link-container'>${socialMediaMarkup}</div>`;
    }

    return `
      ${linksMarkup}
      ${socialMediaMarkup}
    `;
  }

  get fullDescriptionMarkup() {
    return `<p class='locview-description'>${this.loc.fullDescription}</p>`;
  }

  get googleMapsLinkMarkup() {
    return `
      <a class='google-link'
         target='_blank'
         href='${this.loc.googleMapsUrl}'>
        View it on Google Maps
      </a>
    `;
  }

  get historyMarkup() {
    let markup = '';
    for (let entry of this.loc.history) {
      markup += `<li>${new Date(entry.date).toLocaleString('en-us')} by ${
        entry.author_name
      } &lt;${entry.author_email}&gt;</li>`;
    }
    return markup;
  }

  get picListMarkup() {
    return `<image-list class='locview-piclist'></image-list>`;
  }

  get mainButtonsMarkup() {
    return `
      <div class='locview-control-buttons'>
        <span tabindex='0'
              class='going-there-button cute-button'>I'm going there</span>
        <span tabindex='0'
              class='share-loc-button cute-button'>
          <img class='button-image'
               src='/images/app_icons.svg#share'>Share
        </span>
      </div>
    `;
  }

  get mosaicCoverMarkup() {
    let inactiveAttr = !this.loc.hasPics() ? 'inactive' : '';
    let picMarkup = this.loc.mosaicPics.map(i => {
      let altAttr = i.alt ? ` alt='${i.alt}'` : '';
      return `<image-mosaic-pic src='${i.url}'${altAttr}></image-mosaic-pic>`;
    });
    return `
      <div class='locview-cover'>
        <div class='aspect-ratio-div'>
          <image-mosaic class='aspect-ratio-div-child' ${inactiveAttr}>
            ${picMarkup}
          </image-mosaic>
        </div>
      </div>
    `;
  }

  get reviewsMarkup() {
    return `
      <div class='locview-reviews-container'>
        <div class='locview-section-label'>Reviews</div>
        <div class='locview-review-items-container'></div>
      </div>
    `;
  }

  get summaryMarkup() {
    const markup = ReviewRatingsViewMarkup.getMarkupFor(
      this.loc.reviews.summary,
      this.loc.attributes
    );
    return `
      <review-ratings-view class='review-summary locview-summary'>${markup}</review-ratings-view>
    `;
  }

  get titleMarkup() {
    let ratingMarkup = '';
    let overallRating = this.loc.overallRating;
    ratingMarkup = `
      <div class='locview-overall-rating-label locview-inferior-label'>${
        overallRating || ''
      }</div>
      <rating-control rating-id='overall'
                      ${(overallRating && `value='${overallRating}'`) || ''}
                      readonly='readonly'></rating-control>
      `;

    return `
      <div class='loading-box'>
        <div class='loading-bar80wide'></div>
        <div class='loading-bar40'></div>
        <div class='loading-bar50'></div>
      </div>
      <div class='locview-title-container content-box'>
        <div class='locview-title'>${this.loc.name}</div>
        <div class='locview-rating'>${ratingMarkup}</div>
        <div>
          <div class='locview-type locview-inferior-label'>${this.loc.typeName}</div>
          ${this.titleRelsMarkup}
        </div>
      </div>
    `;
  }

  get titleRelsMarkup() {
    let markup = '';
    let toponymLoc = this.loc.toponym;
    let { id: waterbodyId, name: waterbodyName } = this.loc.waterbody || {};
    if (waterbodyId) {
      markup += `
        <span class='locview-rel'>
          <span class='locview-rel-prefix locview-inferior-label'>On </span>
          <span class='locview-rel-link link'
                   locid='${waterbodyId}'>${waterbodyName}</span>${
        (toponymLoc && ',') || ''
      }
        </span>
      `;
    }
    if (toponymLoc) {
      let { id, name } = toponymLoc;
      markup += `
        <span class='locview-rel'>
          <span class='locview-rel-link link'
                locid='${id}'>${name}</span>
        </span>
      `;
    }

    return `<div class='locview-rels'>${markup}</div>`;
  }

  #getGenericFieldMarkup(name, value, img) {
    let imgMarkup = (img && `<img src='${img}'>`) || '';
    switch (this.typeOf(value)) {
      case 'email':
        return `<a href='mailto:${value}' target='_blank' rel='noopener'>${name}</a>`;

      case 'url':
        return `
          <a href='${value}' target='_blank' rel='noopener' class='locview-link'>
            ${imgMarkup}${name}
          </a>
        `;

      default:
        return `<span>${name}: ${value}</span>`;
    }
  }

  #getSocialMediaFieldMarkup(name, value) {
    return `
      <a class='${name.toLowerCase()}-ico-link ico-link'
         href='${value}'
         target='_blank' rel='noopener'
         title='${name}'></a>
    `;
  }

  get #locDataStringified() {
    return this.loc.id ? JSON.stringify(this.loc.data) : '';
  }
}

export { LocMarkupView };
